import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import LoginPage from './pages/LoginPage';
import SearchPage from './pages/SearchPage';
import CampaignPage from './pages/CampaignPage';
import CampaignStatusPage from './pages/CampaignStatusPage';
import LeadsPage from './pages/LeadsPage';
import AccountSettingPage from './pages/AccountSettingPage';
import SendersPage from './pages/SendersPage';
import InboxPage from './pages/InboxPage';
import RecurringSeriesPage from './pages/RecurringSeriesPage';
import InvitationsPage from './pages/InvitationsPage';
import SenderSwitcher from './components/SenderSwitcher';
import { API_BASE_URL } from './config/api';
import './App.css';

const SELECTED_ACCOUNT_STORAGE_KEY = 'heyreach.selectedAccountId';

function App() {
  const [accountId, setAccountId] = useState(null);
  const [isLoadingAccount, setIsLoadingAccount] = useState(true);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [isCampaignCreating, setIsCampaignCreating] = useState(false);

  // 初始化时：总是从后端获取最新的 account_id（优先于 localStorage）
  useEffect(() => {
    const fetchCurrentAccount = async () => {
      if (localStorage.getItem('isLoggedOut') === 'true') {
        setIsLoadingAccount(false);
        return;
      }

      try {
        console.log('🔍 从后端获取最新的 account_id...');
        const response = await axios.get(`${API_BASE_URL}/linkedin/current-account`, {
          timeout: 10000 // 10秒超时
        });
        
        if (response.data.success && response.data.accountId) {
          const backendAccountId = response.data.accountId;
          const savedAccountId = localStorage.getItem(SELECTED_ACCOUNT_STORAGE_KEY);
          let activeAccountId = backendAccountId;

          try {
            const accountsResponse = await axios.get(`${API_BASE_URL}/linkedin/accounts`, {
              timeout: 10000
            });
            const senders = accountsResponse.data?.senders || [];
            const isUsable = (sender) => Boolean(sender) && !['CREDENTIALS', 'DISCONNECTED', 'ERROR', 'FAILED']
              .includes(String(sender.status || '').toUpperCase());
            const availableSenders = senders.filter(isUsable);
            const savedSender = senders.find((sender) => sender.account_id === savedAccountId);
            const backendSender = senders.find((sender) => sender.account_id === backendAccountId);

            if (savedSender && isUsable(savedSender)) {
              activeAccountId = savedSender.account_id;
            } else if (savedAccountId) {
              localStorage.removeItem(SELECTED_ACCOUNT_STORAGE_KEY);
            }

            if (!isUsable(senders.find((sender) => sender.account_id === activeAccountId))) {
              activeAccountId = (isUsable(backendSender) && backendSender.account_id)
                || availableSenders[0]?.account_id
                || backendAccountId;
            }
          } catch (accountsError) {
            console.warn('⚠️ 无法验证已保存的账号，使用后端默认账号:', accountsError.message);
          }

          console.log('✅ 从后端获取到 accountId:', backendAccountId);
          
          // 更新 state
          setAccountId(activeAccountId);
          
          // 同步到 localStorage（作为缓存）
          localStorage.setItem('accountId', activeAccountId);
          localStorage.setItem('linkedin_account_id', activeAccountId);
          localStorage.setItem(SELECTED_ACCOUNT_STORAGE_KEY, activeAccountId);
          
          // 如果 localStorage 中的值不同，说明需要更新
          const storedAccountId = localStorage.getItem('accountId');
          if (storedAccountId && storedAccountId !== activeAccountId) {
            console.log(`⚠️ localStorage 中的 accountId (${storedAccountId}) 与当前账号不一致，已更新为: ${activeAccountId}`);
          }
        } else {
          console.log('ℹ️ 后端没有找到已连接的账号');
          // 清除 localStorage
          localStorage.removeItem('accountId');
          localStorage.removeItem('linkedin_account_id');
          localStorage.removeItem(SELECTED_ACCOUNT_STORAGE_KEY);
          setAccountId(null);
        }
      } catch (error) {
        // 区分不同类型的错误
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          console.warn('⏱️ 请求超时（10秒），使用 localStorage 作为后备');
        } else if (error.response) {
          // 服务器返回了错误响应
          console.log('ℹ️ 后端返回错误:', error.response.status, error.response.data?.message || error.message);
        } else if (error.request) {
          // 请求已发出但没有收到响应（网络错误）
          console.warn('⚠️ 网络错误，无法连接到后端:', error.message);
        } else {
          console.log('ℹ️ 无法从后端获取账号:', error.message);
        }
        
        // 如果后端请求失败，尝试使用 localStorage 作为后备
        const storedAccountId = localStorage.getItem(SELECTED_ACCOUNT_STORAGE_KEY)
          || localStorage.getItem('accountId')
          || localStorage.getItem('linkedin_account_id');
        if (storedAccountId) {
          console.log('⚠️ 使用 localStorage 中的 accountId 作为后备:', storedAccountId);
          setAccountId(storedAccountId);
        } else {
          setAccountId(null);
        }
      } finally {
        setIsLoadingAccount(false);
      }
    };

    fetchCurrentAccount();
  }, []);

  // inbox 总未读数（所有账号合计），给 nav 红点用。
  // 走 SSE：有新消息事件就刷新一次；(重)连成功时也刷一次（覆盖断线期间漏掉的事件）。
  // 不再用固定轮询。
  useEffect(() => {
    if (!accountId) {
      setInboxUnread(0);
      return undefined;
    }
    let cancelled = false;
    const fetchTotal = () => {
      axios.get(`${API_BASE_URL}/inbox/unread-count`, { timeout: 10000 })
        .then((res) => { if (!cancelled && res.data.success) setInboxUnread(res.data.total || 0); })
        .catch(() => {});
    };
    const es = new EventSource(`${API_BASE_URL}/inbox/stream`);
    es.onopen = fetchTotal;       // 首次/重连成功
    es.onmessage = fetchTotal;    // 有新消息
    es.onerror = () => {};        // EventSource 自动重连
    return () => { cancelled = true; es.close(); };
  }, [accountId]);

  const handleLoginSuccess = (id) => {
    if (!id) {
      console.error('❌ Login success but accountId is empty!');
      return;
    }
    console.log('✅ Login success, setting accountId:', id);
    localStorage.removeItem('isLoggedOut');
    setAccountId(id);
    localStorage.setItem('accountId', id);
    localStorage.setItem('linkedin_account_id', id); // For backward compatibility
    localStorage.setItem(SELECTED_ACCOUNT_STORAGE_KEY, id);
    console.log('✅ localStorage updated:', {
      accountId: localStorage.getItem('accountId'),
      linkedin_account_id: localStorage.getItem('linkedin_account_id')
    });
  };

  const handleSwitchAccount = (id) => {
    if (isCampaignCreating) return;
    if (!id || id === accountId) return;
    console.log('🔁 Switch sender:', accountId, '->', id);
    setAccountId(id);
    localStorage.setItem('accountId', id);
    localStorage.setItem('linkedin_account_id', id);
    localStorage.setItem(SELECTED_ACCOUNT_STORAGE_KEY, id);
    localStorage.removeItem('isLoggedOut');
  };

  const handleLogout = async () => {
    // ⭐ Logout 时只清除本地状态，不删除 Unipile 账号
    // 这样可以保持账号一致性，下次登录时重用已有账号
    console.log('🔓 Logout: 清除本地状态，保留 Unipile 账号');
    
    // 清除本地存储
    setAccountId(null);
    localStorage.removeItem('accountId');
    localStorage.removeItem('linkedin_account_id');
    localStorage.removeItem(SELECTED_ACCOUNT_STORAGE_KEY);
    localStorage.setItem('isLoggedOut', 'true');
    
    console.log('✅ 本地状态已清除，Unipile 账号保留');
  };

  // 注意：导航栏 JSX 直接内联在下方 return 里。
  // 不要再把它抽成定义在 App 内部的组件（如 const AppLayout = ...），
  // 否则每次 App 重渲染（例如 30s 未读轮询）都会生成新组件类型，
  // 导致整个 Routes 子树卸载重挂、InboxPage 等页面 state 被清空。

  // 如果正在加载账号信息，显示加载状态
  if (isLoadingAccount) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>加载中...</div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        {accountId && (
          <nav className="app-nav">
            <div className="nav-container">
              <div className="nav-brand">LinkedIn Outreach</div>
              <div className="nav-links">
                <Link to="/search" className="nav-link">🔍 Search</Link>
                <Link to="/leads" className="nav-link">📋 Leads</Link>
                <Link to="/campaigns" className="nav-link">🚀 Campaigns</Link>
                <Link to="/recurring-series" className="nav-link">🔁 Series</Link>
                <Link to="/invitations" className="nav-link">Invitations</Link>
                <Link to="/inbox" className="nav-link">
                  📬 Inbox
                  {inboxUnread > 0 && <span className="nav-inbox-badge">{inboxUnread}</span>}
                </Link>
                <Link to="/senders" className="nav-link">👥 Senders</Link>
                <Link to="/settings" className="nav-link">⚙️ Settings</Link>
                <SenderSwitcher
                  accountId={accountId}
                  onSwitch={handleSwitchAccount}
                  disabled={isCampaignCreating}
                />
                <button onClick={handleLogout} className="nav-logout">Logout</button>
              </div>
            </div>
          </nav>
        )}
        <Routes>
            <Route 
              path="/login" 
              element={
                accountId ? 
                  <Navigate to="/search" replace /> : 
                  <LoginPage onLoginSuccess={handleLoginSuccess} />
              } 
            />
            <Route 
              path="/search" 
              element={
                accountId ? 
                  <SearchPage accountId={accountId} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/leads" 
              element={
                accountId ? 
                  <LeadsPage accountId={accountId} /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/campaigns" 
              element={
                accountId ? 
                  <CampaignPage
                    accountId={accountId}
                    onCreationStateChange={setIsCampaignCreating}
                  /> :
                  <Navigate to="/login" replace />
              } 
            />
            <Route 
              path="/campaigns/:id/status" 
              element={
                accountId ? 
                  <CampaignStatusPage /> : 
                  <Navigate to="/login" replace />
              } 
            />
            <Route
              path="/recurring-series"
              element={
                accountId ?
                  <RecurringSeriesPage accountId={accountId} /> :
                  <Navigate to="/login" replace />
              }
            />
            <Route
              path="/invitations"
              element={
                accountId ?
                  <InvitationsPage key={accountId} accountId={accountId} /> :
                  <Navigate to="/login" replace />
              }
            />
            <Route
              path="/settings"
              element={
                accountId ?
                  <AccountSettingPage accountId={accountId} /> :
                  <Navigate to="/login" replace />
              }
            />
            <Route
              path="/inbox"
              element={
                accountId ?
                  <InboxPage initialAccountId={accountId} onAccountChange={handleSwitchAccount} /> :
                  <Navigate to="/login" replace />
              }
            />
            <Route
              path="/senders"
              element={
                accountId ?
                  <SendersPage accountId={accountId} onSwitchAccount={handleSwitchAccount} /> :
                  <Navigate to="/login" replace />
              }
            />
            <Route
              path="/connect-sender"
              element={
                accountId ?
                  <LoginPage
                    mode="add-sender"
                    redirectTo="/senders"
                    onLoginSuccess={handleLoginSuccess}
                  /> :
                  <Navigate to="/login" replace />
              }
            />
            <Route path="*" element={<Navigate to={accountId ? "/search" : "/login"} replace />} />
          </Routes>
      </div>
    </Router>
  );
}

export default App;
